import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getExchangeOptions } from '@/lib/api'
import type { ExchangeFeature, ExchangeOptionsResponse } from '@/types'
import { FALLBACK_EXCHANGES } from '@/types'
import { FALLBACK_EXCHANGE_CATALOG } from '@/lib/exchangeCatalog'

const featureNames: ExchangeFeature[] = [
  'spot_ticker', 'futures_ticker', 'mark_price', 'funding_rate',
  'open_interest', 'orderbook', 'trades', 'klines',
]

const fallbackResponse: ExchangeOptionsResponse = {
  enabled: FALLBACK_EXCHANGES,
  all: FALLBACK_EXCHANGES,
  exchanges: FALLBACK_EXCHANGE_CATALOG,
  by_feature: Object.fromEntries(featureNames.map((feature) => [
    feature, FALLBACK_EXCHANGE_CATALOG.filter((item) => item.features[feature]).map((item) => item.name),
  ])) as Record<ExchangeFeature, string[]>,
}

export function useExchangeOptions(feature?: ExchangeFeature, market?: 'spot' | 'futures'): string[] {
  const { data } = useQuery({
    queryKey: ['exchange-options'],
    queryFn: getExchangeOptions,
    staleTime: 60_000,
  })

  return useMemo(() => {
    const options = data || fallbackResponse
    const values = feature ? options.by_feature?.[feature] : options.enabled
    const source = Array.isArray(values) ? values : options.enabled
    return Array.from(new Set(source.map((exchange) => String(exchange).toLowerCase()).filter(Boolean)))
      .filter((exchange) => {
        if (!feature || !market) return true
        const entry = options.exchanges.find((item) => item.name === exchange)
        const fallback = FALLBACK_EXCHANGE_CATALOG.find((item) => item.name === exchange)
        const markets = entry?.feature_markets?.[feature] ?? fallback?.feature_markets?.[feature]
        return !markets || markets.includes(market)
      })
  }, [data, feature, market])
}

export function useExchangeOptionsData(): ExchangeOptionsResponse {
  const { data } = useQuery({
    queryKey: ['exchange-options'],
    queryFn: getExchangeOptions,
    staleTime: 60_000,
  })
  return data || fallbackResponse
}
